import { FC, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  Clock,
  User,
  FileText,
  CreditCard,
  CheckCircle2,
  AlertCircle,
  MapPin,
} from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import { Spinner } from '../../../components/ui/Spinner';
import { useBooking } from '../../../contexts/BookingContext';
import { useServiceById } from '../../../hooks/useServices';
import { useTherapist } from '../../../hooks/useTherapists';

// Helper to format time for display
function formatTime(time: string): string {
  const parts = time.split(':');
  const hours = parts[0] || '00';
  const minutes = parts[1] || '00';
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
}

// Helper to format date for display
function formatDate(date: Date): string {
  return date.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  STRIPE_CARD: 'Online Card Payment',
  ONSITE_CARD: 'Pay at Location (Card)',
  ONSITE_CASH: 'Cash Payment',
};

export const Step5ReviewConfirm: FC = () => {
  const { formData, isSubmitting, submitError, bookingResult } = useBooking();

  const { data: service, isLoading: isLoadingService } = useServiceById(formData.serviceId);
  const { data: therapist, isLoading: isLoadingTherapist } = useTherapist(
    formData.therapistId || ''
  );

  const isLoading = isLoadingService || isLoadingTherapist;

  // If booking was successful, show success state
  if (bookingResult) {
    return <BookingSuccess bookingResult={bookingResult} />;
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <Spinner size="lg" color="primary" label="Loading summary..." />
      </div>
    );
  }

  const therapistName = therapist?.name || 'Any Available Therapist';

  const totalAmount = service?.basePrice || 0;

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-slate-100">Review Your Booking</h2>
        <p className="mt-1 text-gray-600 dark:text-slate-400">
          Please review the details below before confirming your appointment.
        </p>
      </div>

      {/* Error Message */}
      {submitError && (
        <div className="mb-6 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4" role="alert">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              <h3 className="font-medium text-red-800 dark:text-red-300">Booking Failed</h3>
              <p className="mt-1 text-sm text-red-700 dark:text-red-400">{submitError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Booking Summary Card */}
      <Card variant="elevated" padding="lg" className="mb-6">
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-4 text-lg">Booking Summary</h3>

        <div className="space-y-4">
          {/* Service */}
          <SummaryItem
            icon={FileText}
            label="Service"
            value={service?.name || 'Unknown Service'}
            subValue={service ? `${service.duration} minutes` : undefined}
          />

          {/* Therapist */}
          <SummaryItem
            icon={User}
            label="Therapist"
            value={therapistName}
            subValue={
              !formData.therapistId ? "We'll assign the best available therapist" : undefined
            }
          />

          {/* Date */}
          <SummaryItem
            icon={Calendar}
            label="Date"
            value={formData.date ? formatDate(formData.date) : 'Not selected'}
          />

          {/* Time */}
          <SummaryItem
            icon={Clock}
            label="Time"
            value={
              formData.startTime && formData.endTime
                ? `${formatTime(formData.startTime)} - ${formatTime(formData.endTime)}`
                : 'Not selected'
            }
          />

          {/* Location */}
          <SummaryItem
            icon={MapPin}
            label="Location"
            value="Massatherapy Clinic"
            subValue="123 Wellness Street, London, W1 1AA"
          />

          {/* Payment Method */}
          <SummaryItem
            icon={CreditCard}
            label="Payment"
            value={PAYMENT_METHOD_LABELS[formData.paymentMethod] || 'Unknown'}
          />
        </div>

        {/* Notes */}
        {formData.customerNotes && (
          <div className="mt-6 pt-4 border-t border-gray-100 dark:border-slate-700">
            <h4 className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Your Notes</h4>
            <p className="text-sm text-gray-600 dark:text-slate-400 bg-gray-50 dark:bg-slate-800 rounded-lg p-3">
              {formData.customerNotes}
            </p>
          </div>
        )}
      </Card>

      {/* Price Summary */}
      <Card variant="outlined" padding="md">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-slate-100">Total Amount</h3>
            <p className="text-sm text-gray-500 dark:text-slate-400">
              {formData.paymentMethod === 'STRIPE_CARD' ? 'Due now' : 'Due at appointment'}
            </p>
          </div>
          <div className="text-right">
            <span className="text-2xl font-bold text-primary-600">
              {'\u00A3'}
              {totalAmount.toFixed(2)}
            </span>
            <p className="text-sm text-gray-500 dark:text-slate-400">inc. VAT</p>
          </div>
        </div>

        {formData.paymentMethod === 'STRIPE_CARD' && (
          <div className="mt-4 flex items-center gap-2 text-sm text-gray-600 dark:text-slate-400">
            <CheckCircle2 className="h-4 w-4 text-green-500 dark:text-green-400" aria-hidden="true" />
            <span>Secure payment processed by Stripe</span>
          </div>
        )}
      </Card>

      {/* Cancellation Policy */}
      <div className="mt-6 rounded-lg bg-gray-50 dark:bg-slate-800 p-4">
        <h4 className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Cancellation Policy</h4>
        <p className="text-sm text-gray-600 dark:text-slate-400">
          Free cancellation up to 24 hours before your appointment. Late cancellations or no-shows
          may incur a fee of up to 50% of the booking value.
        </p>
      </div>

      {isSubmitting && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50">
          <Card padding="lg" className="text-center">
            <Spinner size="lg" color="primary" />
            <p className="mt-4 text-gray-700 dark:text-slate-300 font-medium">Confirming your booking...</p>
          </Card>
        </div>
      )}
    </div>
  );
};

interface SummaryItemProps {
  icon: typeof Calendar;
  label: string;
  value: string;
  subValue?: string;
}

const SummaryItem: FC<SummaryItemProps> = ({ icon: Icon, label, value, subValue }) => {
  return (
    <div className="flex items-start gap-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-100 dark:bg-slate-700">
        <Icon className="h-4 w-4 text-gray-600 dark:text-slate-400" aria-hidden="true" />
      </div>
      <div className="flex-1">
        <p className="text-sm text-gray-500 dark:text-slate-400">{label}</p>
        <p className="font-medium text-gray-900 dark:text-slate-100">{value}</p>
        {subValue && <p className="text-sm text-gray-500 dark:text-slate-400">{subValue}</p>}
      </div>
    </div>
  );
};

interface BookingSuccessProps {
  bookingResult: {
    booking: {
      id: string;
      status: string;
      totalAmount: number;
    };
    requiresPayment: boolean;
    paymentAmount: number;
  };
}

const BookingSuccess: FC<BookingSuccessProps> = ({ bookingResult }) => {
  const navigate = useNavigate();

  // Redirect to payment page if online payment is required
  useEffect(() => {
    if (bookingResult.requiresPayment) {
      // Small delay to show success message before redirect
      const timer = setTimeout(() => {
        navigate(`/payment/${bookingResult.booking.id}`);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [bookingResult, navigate]);

  return (
    <div className="text-center py-8">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
        <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" aria-hidden="true" />
      </div>

      <h2 className="mt-6 text-2xl font-bold text-gray-900 dark:text-slate-100">Booking Confirmed!</h2>

      <p className="mt-2 text-gray-600 dark:text-slate-400">Your appointment has been successfully scheduled.</p>

      <div className="mt-6 inline-flex items-center gap-2 rounded-lg bg-gray-100 dark:bg-slate-800 px-4 py-2">
        <span className="text-sm text-gray-600 dark:text-slate-400">Booking Reference:</span>
        <Badge variant="primary" size="lg">
          {bookingResult.booking.id.slice(0, 8).toUpperCase()}
        </Badge>
      </div>

      {bookingResult.requiresPayment && (
        <div className="mt-6 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
          <div className="flex items-center justify-center gap-3">
            <Spinner size="sm" color="primary" />
            <p className="text-amber-800 dark:text-amber-300">
              <strong>Payment Required:</strong> Redirecting to payment page...
            </p>
          </div>
          <p className="mt-2 text-sm text-amber-700 dark:text-amber-400 text-center">
            Amount: {'\u00A3'}
            {bookingResult.paymentAmount.toFixed(2)}
          </p>
        </div>
      )}

      <p className="mt-6 text-sm text-gray-500 dark:text-slate-400">
        A confirmation email has been sent to your registered email address.
      </p>
    </div>
  );
};
